/**
 * The emoji set behind the picker.
 *
 * Written as lines of "<char> <keyword> <keyword>…" because that is the shape
 * a person can actually edit: one emoji per line, keywords in both languages
 * the app speaks, and nothing to keep in sync between two structures. It is
 * parsed once at module load into the arrays the grid renders and the index
 * search walks.
 *
 * Keywords are deliberately not exhaustive. Someone reaching for the picker is
 * either browsing a category or typing the obvious word — "серд", "cat",
 * "огонь" — and a short honest list beats a long generated one nobody checked.
 */

const GROUPS = [
  {
    id: 'smileys',
    tab: '😀',
    items: `
😀 grin улыбка ухмылка
😃 smile улыбка радость
😄 laugh смех радость
😁 beam улыбка зубы
😆 grin squint хохот смех
😅 sweat смех пот
🤣 rofl ржу смех
😂 joy слёзы смех
🙂 slight улыбка
🙃 upside перевёрнутый ирония
😉 wink подмигивание
😊 blush смущение улыбка
😇 halo ангел нимб
🥰 love любовь сердечки
😍 heart eyes влюблён восторг
🤩 star struck восторг звёзды
😘 kiss поцелуй
😗 kissing поцелуй
😚 kissing closed поцелуй
😙 kissing smile поцелуй
🥲 tear улыбка слеза
😋 yum вкусно язык
😛 tongue язык
😜 winking tongue язык шутка
🤪 zany безумие дурачество
😝 squint tongue язык
🤑 money деньги богатство
🤗 hug обнимаю объятия
🤭 oops ой ладонь
🤫 shush тихо секрет
🤔 think думаю размышление
🤐 zipper молчу рот
🤨 raised brow сомнение бровь
😐 neutral нейтрально
😑 expressionless без эмоций
😶 no mouth молчание
😏 smirk ухмылка
😒 unamused недовольство
🙄 eye roll закатить глаза
😬 grimace неловко
🤥 lying врёт нос
😌 relieved облегчение
😔 pensive грусть
😪 sleepy сонный
🤤 drool слюни
😴 sleep сон
😷 mask маска болезнь
🤒 thermometer температура болезнь
🤕 bandage травма
🤢 nausea тошнит
🤮 vomit рвота
🤧 sneeze чихаю
🥵 hot жарко
🥶 cold холодно
🥴 woozy пьяный
😵 dizzy голова кругом
🤯 mind blown взрыв мозга
🤠 cowboy ковбой
🥳 party праздник вечеринка
🥸 disguise маскировка
😎 sunglasses крутой очки
🤓 nerd ботаник очки
🧐 monocle монокль
😕 confused растерянность
😟 worried беспокойство
🙁 frown грусть
☹️ sad печаль
😮 open mouth удивление
😯 hushed удивление
😲 astonished шок
😳 flushed смущение краснею
🥺 pleading умоляю щенячьи
😦 anguished испуг
😧 anguished открытый рот
😨 fearful страх
😰 anxious тревога
😥 sad relieved грусть
😢 cry плачу слеза
😭 sob рыдаю плач
😱 scream ужас крик
😖 confounded мучение
😣 persevere терплю
😞 disappointed разочарование
😓 downcast пот
😩 weary устал
😫 tired устал
🥱 yawn зеваю скука
😤 triumph злость пар
😡 rage злость гнев
😠 angry злой
🤬 swear мат ругань
😈 imp чертёнок
👿 devil чёрт
💀 skull череп смерть
☠️ skull bones череп
💩 poop какашка
🤡 clown клоун
👻 ghost привидение
👽 alien пришелец
👾 invader игра
🤖 robot робот
😺 cat smile кот
😸 cat grin кот
😹 cat joy кот слёзы
😻 cat love кот любовь
😼 cat smirk кот
😽 cat kiss кот поцелуй
🙀 cat scream кот ужас
😿 cat cry кот плачет
😾 cat pout кот злой
🙈 see no evil обезьяна не вижу
🙉 hear no evil обезьяна не слышу
🙊 speak no evil обезьяна не говорю
`
  },
  {
    id: 'people',
    tab: '👋',
    items: `
👋 wave привет пока рука
🤚 raised back рука
🖐 hand рука пальцы
✋ stop стоп рука
🖖 vulcan рука
👌 ok окей
🤌 pinched пальцы
🤏 pinch чуть-чуть
✌️ victory мир два
🤞 fingers crossed удачи
🤟 love you люблю
🤘 rock рок
🤙 call me звони
👈 left влево
👉 right вправо
👆 up вверх
👇 down вниз
☝️ index вверх
👍 thumbs up класс лайк да
👎 thumbs down дизлайк плохо
✊ fist кулак
👊 punch кулак удар
🤛 left fist кулак
🤜 right fist кулак
👏 clap аплодисменты браво
🙌 raised hands ура руки
🫶 heart hands сердце руками
👐 open hands руки
🤲 palms up ладони
🤝 handshake рукопожатие договор
🙏 pray спасибо пожалуйста молюсь
✍️ writing пишу
💅 nails ногти
🤳 selfie селфи
💪 muscle сила бицепс
🦾 mechanical arm рука
🦵 leg нога
🦶 foot ступня
👂 ear ухо
👃 nose нос
🧠 brain мозг
🫀 heart organ сердце
🦷 tooth зуб
🦴 bone кость
👀 eyes глаза смотрю
👁 eye глаз
👅 tongue язык
👄 lips губы
💋 kiss mark поцелуй губы
👶 baby младенец
🧒 child ребёнок
👦 boy мальчик
👧 girl девочка
🧑 person человек
👨 man мужчина
👩 woman женщина
🧔 beard борода
👴 old man дедушка
👵 old woman бабушка
🙅 no нельзя нет
🙆 ok person окей
🙋 raising hand рука вопрос
🙇 bow поклон извини
🤦 facepalm фейспалм
🤷 shrug не знаю пожимаю
👮 police полиция
🕵️ detective детектив
👷 construction строитель
🤴 prince принц
👸 princess принцесса
🎅 santa дед мороз
🤶 mrs claus снегурочка
🦸 superhero супергерой
🦹 supervillain злодей
🧙 mage маг волшебник
🧚 fairy фея
🧛 vampire вампир
🧜 merperson русалка
🧝 elf эльф
🧞 genie джинн
🧟 zombie зомби
💆 massage массаж
💇 haircut стрижка
🚶 walk иду пешком
🏃 run бегу
💃 dancer танец
🕺 dancing man танец
👫 couple пара
👪 family семья
🗣 speaking говорю
👤 silhouette человек
👥 silhouettes люди
`
  },
  {
    id: 'nature',
    tab: '🐻',
    items: `
🐶 dog собака пёс
🐱 cat кот кошка
🐭 mouse мышь
🐹 hamster хомяк
🐰 rabbit кролик заяц
🦊 fox лиса
🐻 bear медведь
🐼 panda панда
🐨 koala коала
🐯 tiger тигр
🦁 lion лев
🐮 cow корова
🐷 pig свинья
🐸 frog лягушка
🐵 monkey обезьяна
🦍 gorilla горилла
🐔 chicken курица
🐧 penguin пингвин
🐦 bird птица
🐤 chick цыплёнок
🦆 duck утка
🦅 eagle орёл
🦉 owl сова
🦇 bat летучая мышь
🐺 wolf волк
🐗 boar кабан
🐴 horse лошадь конь
🦄 unicorn единорог
🐝 bee пчела
🪲 beetle жук
🐛 caterpillar гусеница
🦋 butterfly бабочка
🐌 snail улитка
🐞 ladybug божья коровка
🐜 ant муравей
🦗 cricket кузнечик
🕷 spider паук
🦂 scorpion скорпион
🐢 turtle черепаха
🐍 snake змея
🦎 lizard ящерица
🦖 dinosaur динозавр
🐙 octopus осьминог
🦑 squid кальмар
🦐 shrimp креветка
🦀 crab краб
🐡 blowfish рыба
🐠 tropical fish рыба
🐟 fish рыба
🐬 dolphin дельфин
🐳 whale кит
🦈 shark акула
🐊 crocodile крокодил
🐆 leopard леопард
🦓 zebra зебра
🐘 elephant слон
🦛 hippo бегемот
🦏 rhino носорог
🐪 camel верблюд
🦒 giraffe жираф
🦘 kangaroo кенгуру
🐑 sheep овца
🦙 llama лама
🐐 goat коза
🦌 deer олень
🐕 dog собака
🐩 poodle пудель
🐈 cat кошка
🐓 rooster петух
🦃 turkey индейка
🦚 peacock павлин
🦜 parrot попугай
🦢 swan лебедь
🕊 dove голубь мир
🦝 raccoon енот
🦨 skunk скунс
🦔 hedgehog ёж
🐾 paws лапки следы
🌵 cactus кактус
🎄 christmas tree ёлка новый год
🌲 evergreen ель сосна pine
🌳 tree дерево
🌴 palm пальма
🪴 potted plant растение
🌱 seedling росток
🌿 herb трава
☘️ shamrock клевер
🍀 four leaf клевер удача
🍃 leaves листья ветер
🍂 fallen leaves листья осень
🍁 maple клён осень
🍄 mushroom гриб
🌾 grain колос
💐 bouquet букет цветы
🌷 tulip тюльпан
🌹 rose роза
🥀 wilted увядший цветок
🌺 hibiscus цветок
🌸 blossom сакура цветок
🌼 daisy ромашка
🌻 sunflower подсолнух
🌞 sun face солнце
🌝 moon face луна
🌙 crescent луна месяц
⭐ star звезда
🌟 glowing star звезда
✨ sparkles искры блеск
⚡ zap молния
🔥 fire огонь
💥 boom взрыв
☄️ comet комета
🌈 rainbow радуга
☀️ sunny солнце ясно
🌤 sun cloud солнце облако
⛅ partly cloudy облачно
☁️ cloud облако
🌧 rain дождь
⛈ thunderstorm гроза
🌨 snow cloud снег
❄️ snowflake снежинка
☃️ snowman снеговик
⛄ snowman снеговик
🌬 wind ветер
🌪 tornado торнадо
🌫 fog туман
🌊 wave волна море
💧 droplet капля
`
  },
  {
    id: 'food',
    tab: '🍔',
    items: `
🍏 green apple яблоко
🍎 apple яблоко
🍐 pear груша
🍊 tangerine мандарин апельсин
🍋 lemon лимон
🍌 banana банан
🍉 watermelon арбуз
🍇 grapes виноград
🍓 strawberry клубника
🫐 blueberries черника
🍈 melon дыня
🍒 cherries вишня черешня
🍑 peach персик
🥭 mango манго
🍍 pineapple ананас
🥥 coconut кокос
🥝 kiwi киви
🍅 tomato помидор
🍆 eggplant баклажан
🥑 avocado авокадо
🥦 broccoli брокколи
🥬 greens зелень салат
🥒 cucumber огурец
🌶 pepper перец
🌽 corn кукуруза
🥕 carrot морковь
🧄 garlic чеснок
🧅 onion лук
🥔 potato картошка
🍠 sweet potato батат
🥐 croissant круассан
🥯 bagel бублик
🍞 bread хлеб
🥖 baguette багет
🥨 pretzel крендель
🧀 cheese сыр
🥚 egg яйцо
🍳 fried egg яичница
🧈 butter масло
🥞 pancakes блины оладьи
🧇 waffle вафля
🥓 bacon бекон
🥩 steak мясо стейк
🍗 poultry курица ножка
🍖 meat мясо
🌭 hotdog хот-дог
🍔 burger бургер
🍟 fries картошка фри
🍕 pizza пицца
🥪 sandwich бутерброд
🌮 taco тако
🌯 burrito буррито
🥙 pita шаурма
🧆 falafel фалафель
🥗 salad салат
🍲 stew суп
🍜 ramen лапша
🍝 spaghetti паста макароны
🍣 sushi суши
🍱 bento бенто
🍛 curry карри
🍚 rice рис
🥟 dumpling пельмени
🍤 fried shrimp креветка
🦪 oyster устрица
🍦 ice cream мороженое
🍨 ice cream мороженое
🥧 pie пирог
🧁 cupcake кекс
🍰 cake торт пирожное
🎂 birthday торт день рождения
🍮 custard пудинг
🍪 cookie печенье
🍫 chocolate шоколад
🍬 candy конфета
🍭 lollipop леденец
🍯 honey мёд
🥛 milk молоко
☕ coffee кофе
🫖 teapot чайник
🍵 tea чай
🧃 juice сок
🥤 soda газировка
🧋 bubble tea чай
🍺 beer пиво
🍻 beers пиво
🍷 wine вино
🥂 champagne шампанское тост
🥃 whisky виски
🍸 cocktail коктейль
🍾 bottle шампанское
🧊 ice лёд
`
  },
  {
    id: 'travel',
    tab: '✈️',
    items: `
🚗 car машина авто
🚕 taxi такси
🚌 bus автобус
🚎 trolleybus троллейбус
🏎 race car гонка
🚓 police car полиция
🚑 ambulance скорая
🚒 fire engine пожарная
🚐 minibus маршрутка
🚚 truck грузовик
🚜 tractor трактор
🛴 scooter самокат
🚲 bike велосипед
🛵 moped мопед
🏍 motorcycle мотоцикл
🚨 siren мигалка
🚄 bullet train поезд
🚂 locomotive паровоз
🚋 tram трамвай
🚇 metro метро
🚏 bus stop остановка
🛤 railway рельсы
🛣 motorway трасса
⛽ fuel заправка бензин
🚦 traffic light светофор
🚧 construction ремонт
⚓ anchor якорь
⛵ sailboat парусник
🛶 canoe каноэ
🚤 speedboat катер
🛳 ship корабль
⛴ ferry паром
🚢 ship корабль
✈️ airplane самолёт
🛫 takeoff взлёт
🛬 landing посадка
🪂 parachute парашют
💺 seat кресло место
🚁 helicopter вертолёт
🚀 rocket ракета
🛸 ufo нло
🛰 satellite спутник
🧳 luggage чемодан багаж
🗺 map карта
🧭 compass компас
🏔 mountain гора
⛰ mountain гора
🌋 volcano вулкан
🏕 camping кемпинг палатка
🏖 beach пляж
🏝 island остров
🏜 desert пустыня
🏗 construction site стройка
🏠 house дом
🏡 house garden дом
🏢 office офис
🏥 hospital больница
🏦 bank банк
🏨 hotel отель
🏫 school школа
🏭 factory завод
🏰 castle замок
⛪ church церковь
🕌 mosque мечеть
🗽 statue liberty статуя
🗼 tower башня
🌉 bridge мост
🌃 night ночь город
🌆 city город
🌇 sunset закат
🌅 sunrise рассвет
🎆 fireworks салют фейерверк
🎇 sparkler бенгальский
`
  },
  {
    id: 'activities',
    tab: '⚽',
    items: `
⚽ soccer футбол мяч
🏀 basketball баскетбол
🏈 football американский
⚾ baseball бейсбол
🎾 tennis теннис
🏐 volleyball волейбол
🏉 rugby регби
🥏 frisbee фрисби
🎱 pool бильярд
🏓 ping pong настольный теннис
🏸 badminton бадминтон
🥅 goal ворота
🏒 hockey хоккей
🏑 field hockey хоккей
🏏 cricket крикет
⛳ golf гольф
🏹 archery лук стрелы
🎣 fishing рыбалка
🥊 boxing бокс
🥋 martial arts карате
🛹 skateboard скейт
🛼 roller skate ролики
⛸ ice skate коньки
🎿 ski лыжи
🏂 snowboard сноуборд
🏋️ weightlifting штанга зал
🤸 cartwheel гимнастика
⛹️ basketball player баскетбол
🤺 fencing фехтование
🤾 handball гандбол
🏌️ golfer гольф
🏇 horse racing скачки
🧘 yoga йога медитация
🏄 surf сёрфинг
🏊 swim плавание
🚣 rowing гребля
🧗 climbing альпинизм
🚵 mountain bike велосипед
🚴 cycling велосипед
🏆 trophy кубок победа
🥇 gold золото первое
🥈 silver серебро второе
🥉 bronze бронза третье
🎖 medal медаль
🏅 sports medal медаль
🎯 dart дартс цель
🎮 video game игра геймпад
🕹 joystick джойстик
🎲 dice кубик
♟ chess шахматы
🧩 puzzle пазл
🎭 theatre театр маски
🎨 art искусство палитра
🎬 clapper кино
🎤 microphone микрофон караоке
🎧 headphones наушники
🎼 sheet music ноты
🎹 piano пианино
🥁 drum барабан
🎷 sax саксофон
🎺 trumpet труба
🎸 guitar гитара
🎻 violin скрипка
🎪 circus цирк
🎟 ticket билет
🎁 gift подарок
🎈 balloon шарик
🎉 tada праздник ура
🎊 confetti конфетти
🎃 halloween тыква
🧨 firecracker петарда
`
  },
  {
    id: 'objects',
    tab: '💡',
    items: `
⌚ watch часы
📱 phone телефон
💻 laptop ноутбук
⌨️ keyboard клавиатура
🖥 desktop компьютер
🖨 printer принтер
🖱 mouse мышка
💾 floppy дискета сохранить
💿 cd диск
📀 dvd диск
📷 camera фото
📸 camera flash фото
📹 video camera видео
🎥 movie camera кино
📺 tv телевизор
📻 radio радио
🎙 studio mic микрофон
⏱ stopwatch секундомер
⏰ alarm будильник
🔋 battery батарея
🔌 plug розетка
💡 bulb лампочка идея
🔦 flashlight фонарик
🕯 candle свеча
🧯 extinguisher огнетушитель
💸 money wings деньги улетают
💵 dollar доллар деньги
💶 euro евро
💷 pound фунт
💰 money bag мешок денег
💳 credit card карта
🧾 receipt чек
💎 gem алмаз
⚖️ scales весы
🔧 wrench гаечный ключ
🔨 hammer молоток
🛠 tools инструменты
⛏ pick кирка
🔩 nut bolt болт
⚙️ gear шестерёнка настройки
🧰 toolbox ящик инструментов
🧲 magnet магнит
💣 bomb бомба
🔪 knife нож
🛡 shield щит
🔮 crystal ball шар предсказание
🔭 telescope телескоп
🔬 microscope микроскоп
💊 pill таблетка
💉 syringe шприц укол
🩸 blood кровь
🧬 dna днк
🦠 microbe микроб вирус
🧪 test tube пробирка
🌡 thermometer термометр
🧹 broom метла
🧺 basket корзина
🚿 shower душ
🛁 bathtub ванна
🧼 soap мыло
🪥 toothbrush щётка
🔑 key ключ
🗝 old key ключ
🚪 door дверь
🪑 chair стул
🛏 bed кровать
🛋 couch диван
🖼 picture картина
🛍 shopping покупки
🎒 backpack рюкзак
👓 glasses очки
🕶 sunglasses очки
👔 necktie галстук
👕 tshirt футболка
👖 jeans джинсы
🧣 scarf шарф
🧤 gloves перчатки
🧥 coat пальто
👗 dress платье
👠 heel туфли
👟 sneaker кроссовки
👢 boot сапог
🎩 top hat цилиндр
🎓 graduation выпускной диплом
👑 crown корона
💍 ring кольцо
🌂 umbrella зонт
📚 books книги
📖 open book книга
📕 closed book книга
📝 memo заметка запись
✏️ pencil карандаш
🖊 pen ручка
📌 pushpin кнопка
📍 pin метка место
📎 paperclip скрепка
✂️ scissors ножницы
📏 ruler линейка
🗂 dividers папки
📁 folder папка
📂 open folder папка
📅 calendar календарь
📆 calendar календарь
📈 chart up график рост
📉 chart down график падение
📊 bar chart диаграмма статистика
📋 clipboard список
📦 package коробка посылка
✉️ envelope конверт письмо
📧 email почта
📮 postbox почта
📢 loudspeaker громкоговоритель
📣 megaphone мегафон
🔔 bell колокольчик уведомление
🔕 bell off без звука
🔍 search поиск лупа
🔒 lock замок закрыто
🔓 unlock открыто
🔐 locked key ключ
🔗 link ссылка
`
  },
  {
    id: 'symbols',
    tab: '❤️',
    items: `
❤️ red heart сердце любовь
🧡 orange heart сердце
💛 yellow heart сердце
💚 green heart сердце
💙 blue heart сердце
💜 purple heart сердце
🖤 black heart сердце
🤍 white heart сердце
🤎 brown heart сердце
💔 broken heart разбитое сердце
❣️ heart exclamation сердце
💕 two hearts сердечки
💞 revolving hearts сердечки
💓 beating heart сердце
💗 growing heart сердце
💖 sparkling heart сердце
💘 cupid стрела сердце
💝 heart gift подарок сердце
☮️ peace мир
✝️ cross крест
☪️ crescent star полумесяц
☸️ dharma колесо
✡️ star david звезда
☯️ yin yang инь янь
♈ aries овен
♉ taurus телец
♊ gemini близнецы
♋ cancer рак
♌ leo лев
♍ virgo дева
♎ libra весы
♏ scorpio скорпион
♐ sagittarius стрелец
♑ capricorn козерог
♒ aquarius водолей
♓ pisces рыбы
⚛️ atom атом
☢️ radioactive радиация
☣️ biohazard опасность
🔞 no under 18 возраст
🚫 prohibited запрещено нельзя
❌ x нет ошибка крест
⭕ o круг
🛑 stop стоп
⛔ no entry въезд запрещён
♻️ recycle переработка
✅ check готово да галочка
☑️ ballot галочка
✔️ check галочка
🌐 globe глобус интернет
💤 zzz сон
🏧 atm банкомат
♿ wheelchair доступность
🅿️ parking парковка
🛗 elevator лифт
⚠️ warning внимание предупреждение
🔱 trident трезубец
♠️ spades пики
♥️ hearts червы
♦️ diamonds бубны
♣️ clubs трефы
🃏 joker джокер
🔇 mute без звука
🔊 speaker громко звук
📶 signal сигнал связь
🔅 dim тускло
🔆 bright ярко
♾️ infinity бесконечность
💱 currency обмен валют
™️ tm
©️ copyright
®️ registered
🔚 end конец
🔙 back назад
🔝 top наверх
🔜 soon скоро
❓ question вопрос
❗ exclamation восклицание
‼️ double exclamation
⁉️ interrobang
💯 hundred сто отлично
🔢 numbers цифры
🔣 symbols символы
▶️ play плей
⏸ pause пауза
⏹ stop стоп
⏺ record запись
⏭ next следующий
⏮ previous предыдущий
⏩ fast forward перемотка
⏪ rewind перемотка
🔀 shuffle перемешать
🔁 repeat повтор
⬆️ up вверх
⬇️ down вниз
⬅️ left влево
➡️ right вправо
🔄 refresh обновить
↩️ back назад
⏳ hourglass ждём песочные часы
⌛ hourglass песочные часы
⏲ timer таймер
🔴 red circle красный круг
🟠 orange circle оранжевый
🟡 yellow circle жёлтый
🟢 green circle зелёный
🔵 blue circle синий
🟣 purple circle фиолетовый
⚫ black circle чёрный
⚪ white circle белый
🟥 red square красный квадрат
🟩 green square зелёный квадрат
🟦 blue square синий квадрат
⬛ black square чёрный квадрат
⬜ white square белый квадрат
🔶 orange diamond ромб
🔷 blue diamond ромб
🏁 checkered flag финиш флаг
🚩 triangular flag флаг
🏳️ white flag белый флаг
🏴 black flag чёрный флаг
`
  }
]

/** Parse a group's lines into [{ char, words }], dropping duplicates. */
function parse (block) {
  const seen = new Set()
  const out = []
  for (const line of block.trim().split('\n')) {
    const text = line.trim()
    if (!text) continue
    const space = text.indexOf(' ')
    const char = space === -1 ? text : text.slice(0, space)
    if (seen.has(char)) continue
    seen.add(char)
    out.push({ char, words: space === -1 ? '' : text.slice(space + 1).toLowerCase() })
  }
  return out
}

const PARSED = GROUPS.map((group) => ({ ...group, entries: parse(group.items) }))

/** What the grid renders: a tab glyph and the characters under it. */
export const CATEGORIES = PARSED.map((group) => ({
  id: group.id,
  tab: group.tab,
  chars: group.entries.map((entry) => entry.char)
}))

/** Every emoji once, in category order — what search walks. */
const INDEX = []
{
  const seen = new Set()
  for (const group of PARSED) {
    for (const entry of group.entries) {
      if (seen.has(entry.char)) continue
      seen.add(entry.char)
      INDEX.push(entry)
    }
  }
}

export const EMOJI_COUNT = INDEX.length

/**
 * Find emoji by keyword.
 *
 * A word that starts with the query ranks above one that merely contains it,
 * so "кот" puts the cat first rather than burying it under everything with
 * those three letters somewhere inside. Pasting an emoji in finds that emoji,
 * which is how someone looks one up again after seeing it in a message.
 */
export function searchEmoji (query, limit = 120) {
  const needle = String(query || '').trim().toLowerCase()
  if (!needle) return []

  const starts = []
  const contains = []

  for (const item of INDEX) {
    if (item.char === needle) {
      starts.unshift(item)
      continue
    }
    let matched = 0
    for (const word of item.words.split(' ')) {
      if (!word) continue
      if (word.startsWith(needle)) { matched = 2; break }
      if (word.includes(needle)) matched = 1
    }
    if (matched === 2) starts.push(item)
    else if (matched === 1) contains.push(item)
    if (starts.length >= limit) break
  }

  return [...starts, ...contains].slice(0, limit).map((item) => item.char)
}

/* ------------------------------------------------------------------ *
 * Recently used
 * ------------------------------------------------------------------ */

const RECENT_KEY = 'pine.emoji.recent'
const RECENT_MAX = 32

/**
 * Kept in localStorage rather than in the room index.
 *
 * It is a per-device convenience with no meaning to anyone else, it has to be
 * readable synchronously on the panel's first render, and losing it costs
 * nothing. Every access is guarded: a WebView with site data blocked throws on
 * touching the property, not merely on the read.
 */
export function loadRecent () {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    const list = raw ? JSON.parse(raw) : []
    return Array.isArray(list) ? list.filter((c) => typeof c === 'string').slice(0, RECENT_MAX) : []
  } catch {
    return []
  }
}

export function pushRecent (char) {
  const next = [char, ...loadRecent().filter((c) => c !== char)].slice(0, RECENT_MAX)
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch { /* private mode, blocked storage — the list is simply not kept */ }
  return next
}
